import { ChangeEvent, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { IMPOSSIBLE_DATE_MESSAGE } from './date-picker.constants';
import { DatePickerData } from './date-picker-data.interface';
import { DatePickerProps } from './date-picker.props';
import {
  countDigits,
  findPositionAfterDigits,
  formatCalendarDate,
  formatDisplayedDate,
  isTypedDateComplete,
  maskTypedDate,
  parseCalendarDate,
  parseTypedDate,
} from './date-picker.utils';

const useDatePicker = (props: DatePickerProps): DatePickerData => {
  const { value, onChange, onTypingErrorChange } = props;
  const date = parseCalendarDate(value);
  const displayedDate = formatDisplayedDate(date);
  const [typedDate, setTypedDate] = useState(displayedDate);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
  const isTypedDateInvalid = isTypedDateComplete(typedDate) && !parseTypedDate(typedDate);
  const typingErrorMessage = (isTypedDateInvalid && IMPOSSIBLE_DATE_MESSAGE) || '';

  useEffect(() => {
    setTypedDate(displayedDate);
  }, [displayedDate]);

  useEffect(() => {
    onTypingErrorChange?.(typingErrorMessage);
  }, [typingErrorMessage]);

  const handleTypedDateChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = event.target;
    const digitsBeforeCaret = countDigits(input.value.slice(0, input.selectionStart || 0));
    const nextTypedDate = maskTypedDate(input.value);
    const typedDay = parseTypedDate(nextTypedDate);
    const nextCaretPosition = findPositionAfterDigits(digitsBeforeCaret);

    input.value = nextTypedDate;
    input.setSelectionRange(nextCaretPosition, nextCaretPosition);
    setTypedDate(nextTypedDate);

    if (typedDay) onChange(formatCalendarDate(typedDay));

    if (!nextTypedDate) onChange('');
  };

  const handleTypedDateBlur = () => {
    setTypedDate(displayedDate);
  };

  const handleTypedDateKeyDown = (event: KeyboardEvent) => {
    const hasUnappliedTyping = typedDate !== displayedDate;

    if (event.key !== 'Enter' || !hasUnappliedTyping) return;

    event.preventDefault();
    setTypedDate(displayedDate);
  };

  const handleOpenCalendar = (event: MouseEvent<HTMLElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setCalendarAnchorEl(null);
  };

  const handleSelectDay = (day: Date) => {
    onChange(formatCalendarDate(day));
    handleCloseCalendar();
  };

  const handleClear = () => {
    onChange('');
    handleCloseCalendar();
  };

  return {
    date,
    typedDate,
    isTypedDateInvalid,
    calendarAnchorEl,
    handleTypedDateChange,
    handleTypedDateBlur,
    handleTypedDateKeyDown,
    handleOpenCalendar,
    handleCloseCalendar,
    handleSelectDay,
    handleClear,
  };
};

export default useDatePicker;
